import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  TextField,
  Alert,
  InputAdornment
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CommonButton from "../../components/CommonButton/CommonButton";
import { apiCall } from "../../utils/apiCall";

interface ForgotPasswordInFirebaseProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessEmail: string;
  setParrentNotificationMessage: ({
    message,
    type
  }: {
    message: string;
    type: string;
  }) => void;
}

const ForgotPasswordInFirebase: React.FC<ForgotPasswordInFirebaseProps> = ({
  open,
  onClose,
  businessId,
  businessEmail,
  setParrentNotificationMessage = () => {}
}) => {
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });

  const validatePassword = (password: string) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    }
    if (password.length < 8 || password.length > 16) {
      setPasswordError("Password must be between 8 and 16 characters");
      return false;
    }
    if (!/(?=.*[a-z])/.test(password)) {
      setPasswordError("Password must contain at least 1 lowercase letter");
      return false;
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      setPasswordError("Password must contain at least 1 uppercase letter");
      return false;
    }
    if (!/(?=.*\d)/.test(password)) {
      setPasswordError("Password must contain at least 1 number");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResetPassword = async () => {
    if (!validatePassword(newPassword)) {
      return;
    }

    setIsLoading(true);
    try {
      const data = {
        Email: businessEmail,
        Password: newPassword
      };

      const response = await apiCall(
        `${process.env.REACT_APP_API_BASE_URL}/users/support/resetpwd`,
        "POST",
        data
      );

      if (response) {
        setParrentNotificationMessage({
          message: "Password reset successfully!",
          type: "success"
        });
        setNewPassword("");
        setPasswordError("");
        setShowPassword(false);
        setNotificationMessage({ message: "", type: "" });
        onClose();
      } else {
        throw new Error("Failed to reset password");
      }
    } catch (error: any) {
      console.error("Password reset error:", error);
      setNotificationMessage({
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to reset password. Please try again.",
        type: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setNewPassword("");
      setPasswordError("");
      setShowPassword(false);
      setNotificationMessage({ message: "", type: "" });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "24px",
              lineHeight: 1.25,
              fontFamily: "Montserrat",
              fontStyle: "normal",
              color: "#192F67"
            }}
          >
            Reset Password in Firebase
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: (theme) =>
                isLoading ? theme.palette.grey[400] : theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          sx={{
            fontSize: "14px",
            textAlign: "center",
            marginTop: "10px",
            marginBottom: "24px",
            lineHeight: 1.4,
            opacity: 0.8
          }}
        >
          Password must be between 8 and 16 characters which contains at least 1
          lowercase letter, 1 uppercase letter, and 1 number.
        </Typography>

        <TextField
          fullWidth
          label="New Password"
          type={showPassword ? "text" : "password"}
          value={newPassword}
          onChange={handlePasswordChange}
          disabled={isLoading}
          error={!!passwordError}
          helperText={passwordError}
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={togglePasswordVisibility}
                  edge="end"
                  disabled={isLoading}
                  sx={{ color: "#1976d2" }}
                >
                  {showPassword ? (
                    <VisibilityOffIcon sx={{ color: "#223354" }} />
                  ) : (
                    <VisibilityIcon sx={{ color: "#223354" }} />
                  )}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {notificationMessage?.message && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={notificationMessage?.type as any}>
              {notificationMessage?.message}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <CommonButton
          onClick={handleClose}
          disabled={isLoading}
          variant="contained"
          sx={{
            backgroundColor: "#f5f5f5",
            color: "#333",
            "&:hover": {
              backgroundColor: "#e0e0e0"
            }
          }}
        >
          Cancel
        </CommonButton>
        <CommonButton
          onClick={handleResetPassword}
          disabled={isLoading || !newPassword || !!passwordError}
          isLoading={isLoading}
          variant="contained"
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            "&:hover": {
              backgroundColor: "#1565c0"
            },
            minHeight: "36px"
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Reset Password"
          )}
        </CommonButton>
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordInFirebase;
